/**
 * useTaskDecision — the single-task decision mutations behind the Aufgaben peek
 * DecisionBar (STEP 3): Erledigen (complete) · In Arbeit (in-progress) · Abbrechen
 * (cancel) · Wieder öffnen (reopen). live mode fires the matching POST /tasks/:id/*
 * and invalidates the worklist + the task detail; mock mode resolves synchronously
 * (no network — the queue stays explorable, mirroring useTaskList / useCreateTask).
 *
 * The UI renders the SERVER's resulting status — it never computes a transition
 * client-side (coding-standards rule #2). Auto-Next (advance to the next task) is
 * the caller's concern, run on a successful terminal decision.
 *
 * [Source: apps/web/src/features/communication/hooks/useTaskActions.ts (mutation + invalidate)]
 * [Source: apps/web/src/features/communication-v2/hooks/useEingangMails.ts (useEingangDecision live/mock)]
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TaskSummary } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';

export type TaskDecisionAction = 'complete' | 'inProgress' | 'cancel' | 'reopen';

function fire(id: string, action: TaskDecisionAction): Promise<TaskSummary> {
  switch (action) {
    case 'complete':
      return apiClient.tasks.complete(id);
    case 'inProgress':
      return apiClient.tasks.inProgress(id);
    case 'cancel':
      return apiClient.tasks.cancel(id);
    case 'reopen':
      return apiClient.tasks.reopen(id);
  }
}

/** Fire one decision action against one task. Invalidates the list + the detail. */
export function useTaskDecision() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation<TaskSummary | null, Error, { id: string; action: TaskDecisionAction }>({
    mutationFn: ({ id, action }) => (live ? fire(id, action) : Promise.resolve(null)),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['tasks', 'list'] });
      void qc.invalidateQueries({ queryKey: qk.task(id) });
    },
  });
}
